import { Component, Input, Output, EventEmitter, forwardRef, ElementRef } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-single-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './single-select.component.html',
  styleUrl: './single-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelectComponent),
      multi: true
    }
  ]
})
export class SingleSelectComponent implements ControlValueAccessor {
  @Input() options: any[] = [];
  @Input() optionLabel: string | undefined = undefined;
  @Input() placeholder: string = 'Select option';
  @Input() styleClass: string = '';
  @Input() disabled: boolean = false;
  @Input() showClear: boolean = true;

  @Output() onChange = new EventEmitter<any>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  selectedValue: any = null;
  isOpen = false;
  
  private onChangeCallback = (value: any) => {};
  private onTouchedCallback = () => {};

  constructor(private elementRef: ElementRef) {
    document.addEventListener('click', (e: MouseEvent) => this.onDocumentClick(e), true);
  }

  /** Capture phase so sibling filter controls still count as outside-click when bubble is stopped upstream. */
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      const wasOpen = this.isOpen;
      this.isOpen = !this.isOpen;
      if (this.isOpen && !wasOpen) {
        this.onTouchedCallback();
        this.onOpen.emit();
      } else if (!this.isOpen && wasOpen) {
        this.onClose.emit();
      }
    }
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.onClose.emit();
    }
  }

  getDisplayValue(): string {
    if (!this.selectedValue) {
      return this.placeholder;
    }
    return this.getOptionLabel(this.selectedValue);
  }

  getOptionLabel(option: any): string {
    if (this.optionLabel && typeof option === 'object' && option !== null) {
      return option[this.optionLabel] || String(option);
    }
    return String(option);
  }

  getOptionValue(option: any): any {
    return option;
  }

  isSelected(option: any): boolean {
    if (!this.selectedValue) {
      return false;
    }
    const optionValue = this.getOptionValue(option);
    
    // Compare by value property if both are objects with value
    if (typeof this.selectedValue === 'object' && typeof optionValue === 'object' && 
        this.selectedValue !== null && optionValue !== null) {
      if ('value' in this.selectedValue && 'value' in optionValue) {
        return this.selectedValue.value === optionValue.value;
      }
      if (this.optionLabel) {
        return this.selectedValue[this.optionLabel] === optionValue[this.optionLabel];
      }
    }
    
    // Direct comparison
    if (typeof this.selectedValue === 'object' && this.selectedValue !== null && 'value' in this.selectedValue) {
      const selectedVal = this.selectedValue.value;
      if (typeof optionValue === 'object' && optionValue !== null && 'value' in optionValue) {
        return selectedVal === optionValue.value;
      }
      return selectedVal === optionValue;
    }
    
    return this.selectedValue === optionValue;
  }

  selectOption(option: any): void {
    if (this.disabled) {
      return;
    }
    
    const optionValue = this.getOptionValue(option);
    
    // If clicking the same option, deselect it
    if (this.isSelected(option)) {
      this.selectedValue = null;
      this.onChangeCallback(null);
      this.onChange.emit(null);
    } else {
      this.selectedValue = optionValue;
      // Return the value property if it exists, otherwise the whole object
      const formValue = typeof optionValue === 'object' && optionValue !== null && 'value' in optionValue 
        ? optionValue.value 
        : optionValue;
      this.onChangeCallback(formValue);
      this.onChange.emit(formValue);
    }
    
    this.closeDropdown();
  }

  clearSelection(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedValue = null;
    this.onChangeCallback(null);
    this.onChange.emit(null);
  }

  trackByOption(index: number, option: any): any {
    if (this.optionLabel && typeof option === 'object' && option !== null) {
      return option[this.optionLabel] || index;
    }
    return option || index;
  }

  getOptionClasses(option: any): string {
    const baseClasses = 'flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors';
    if (this.isSelected(option)) {
      return `${baseClasses} bg-[#f0f7f5]`;
    }
    return baseClasses;
  }

  writeValue(value: any): void {
    if (!value) {
      this.selectedValue = null;
      return;
    }
    
    // If value is a string/number, find the matching option
    if (typeof value === 'string' || typeof value === 'number') {
      const option = this.options.find(opt => {
        const optValue = this.getOptionValue(opt);
        if (this.optionLabel && typeof optValue === 'object') {
          return optValue[this.optionLabel] === value || optValue.value === value;
        }
        return optValue === value || (typeof optValue === 'object' && optValue.value === value);
      });
      this.selectedValue = option ? this.getOptionValue(option) : null;
    } else {
      // Value is already an option object
      this.selectedValue = value;
    }
  }
  
  // Helper to get the actual value to store in form
  getFormValue(): any {
    if (!this.selectedValue) {
      return null;
    }
    
    // If option has a 'value' property, return that, otherwise return the whole object
    if (typeof this.selectedValue === 'object' && this.selectedValue !== null && 'value' in this.selectedValue) {
      return this.selectedValue.value;
    }
    
    return this.selectedValue;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
